<template>
  <div class="pt-5">
    <FullHeightRadialLoader :is-loading="isLoading" :loader-msg="$t('settings/settings:duplicate.overview.loading')"/>
    <div v-if="noDuplicates" class="d-flex align-center justify-center">
      <h4 class="primary--text font-weight-bold mb-0 mr-2">{{ $t('settings/settings:duplicate.overview.no-duplicates') }}</h4>
    </div>

    <div class="my-scroller" v-if="duplicates.length > 0">
      <div v-for="(duplicateGroup, idx) in duplicates" :key="idx" >
        <v-sheet min-height="250" color="transparent" v-if="duplicateGroup[groupObjectField][0]">
          <v-lazy
              v-model="duplicateGroup.isActive"
              :options="{ threshold: .5}"
          >
            <v-card outlined class="mb-10 pa-3">
              <v-card-title>
                <h6 class="mb-0 font-weight-bold">{{ duplicateGroup[groupObjectField][0][groupObjectFieldCardTitle] }} ({{ duplicateGroup[groupObjectField].length }})</h6>
              </v-card-title>

              <v-card-text>
                <v-card v-for="item in duplicateGroup[groupObjectField]" :key="item.id" outlined class="mb-2">
                  <v-card-title class="d-flex align-center dupl-header">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-simple-checkbox
                            class="mt-0 pt-0"
                            color="primary"
                            hide-details
                            v-model="item.merge"
                            :disabled="duplicateGroup.primaryObjectId === item.id"
                            v-on="on"
                            v-bind="attrs"
                            :ripple="false"
                        />
                      </template>
                      <span>{{ $t('settings/settings:duplicate.overview.tooltips.involve-in-merge') }}</span>
                    </v-tooltip>

                    <p class="text-subtitle-1">{{ item[groupObjectFieldCardSubtitle] }}</p>
                    <v-spacer></v-spacer>
                    <v-radio-group v-model="duplicateGroup.primaryObjectId" hide-details class="mt-0">
                      <v-radio :value="item.id">
                        <template v-slot:label>
                          <span class="text-subtitle-2">{{ $t('settings/settings:duplicate.overview.label-primary') }}</span>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </v-card-title>
                  <v-card-text>
                    <v-simple-table>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th v-for="(header, index) in headers" :key="index">{{ header.text }}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td v-for="(header, index) in headers" :key="index">
                              <template v-if="header.value === 'checkbox'">
                                <v-checkbox
                                    v-model="item[checkboxOnProperty]"
                                    disabled
                                />
                              </template>
                              <template v-else>
                                {{ item[header.value] }}
                              </template>
                            </td>
                            <td>
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                      icon
                                      color="primary"
                                      v-on="on"
                                      v-bind="attrs"
                                      @click="$emit('showObject', item.id)"
                                  >
                                    <v-icon>open_in_new</v-icon>
                                  </v-btn>
                                </template>
                                <span>{{ $t('buttons:show') }}</span>
                              </v-tooltip>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-card-text>
                </v-card>

                <v-row>
                  <v-spacer></v-spacer>
                  <BaseButton
                      :disabled="duplicateGroup.primaryObjectId == null"
                      color="primary"
                      :btn-text="$t('settings/settings:duplicate.overview.btn-preview')"
                      @clickedButton="generatePreview(duplicateGroup)"
                  />
                </v-row>
              </v-card-text>
            </v-card>
          </v-lazy>
        </v-sheet>
      </div>
    </div>
  </div>
</template>

<script>
import FullHeightRadialLoader from "@/modules/core/components/Loader/FullHeightRadialLoader";
import BaseButton from "@/modules/core/components/Base/BaseButton";
export default {
  name: "SpecificDuplicateOverview",
  props: {
    groupObjectField: String, // how the field is being called where the duplicates are located. E.g. '.buildings[]'
    groupObjectFieldCardTitle: String, // which field of the duplicates should be used as the main card title. E.g. 'name'
    groupObjectFieldCardSubtitle: String, // which field of the duplicates should be used as the sub card title. E.g. 'name'
    headers: Array, // headers for the table header
    checkboxOnProperty: String
  },
  components: {BaseButton, FullHeightRadialLoader},
  data() {
    return {
      duplicates: [],
      isLoading: false,
      noDuplicates: false
    }
  },
  methods: {
    removeDuplicateGroup(idsToRemove) {
      let newDuplicateLength = this.duplicates.length;

      for (let i = this.duplicates.length - 1; i >= 0; i--) {
        // remove deleted objects from other groups
        for (let j = this.duplicates[i][this.groupObjectField].length - 1; j >= 0; j--) {
          if (idsToRemove.indexOf(this.duplicates[i][this.groupObjectField][j].id) !== -1) {
            this.duplicates[i][this.groupObjectField].splice(j, 1)
          }
        }

        // if there is only one object left in the group, remove the whole group
        if (this.duplicates[i][this.groupObjectField].length === 1) {
          this.duplicates.splice(i, 1);
          newDuplicateLength--;
        }
      }

      this.$emit('updateDuplicateLength', newDuplicateLength);
    },

    generatePreview(duplicateGroup) {
      // only pass secondary objects, where property 'merge' is true
      let objectsToMerge = [];
      for (let i = 0; i < duplicateGroup[this.groupObjectField].length; i++) {
        if (duplicateGroup[this.groupObjectField][i].merge
          && duplicateGroup[this.groupObjectField][i].id !== duplicateGroup.primaryObjectId) {
          objectsToMerge.push(duplicateGroup[this.groupObjectField][i]);
        }
      }

      // have to make a copy here because otherwise
      // if the user clicks back in BuildingMerging
      // this group can no longer be found in the overview
      let groupToMerge = {
        primaryObjectId: duplicateGroup.primaryObjectId,
        objects: objectsToMerge
      }

      this.$emit('showPreview', groupToMerge);
    }
  }
}
</script>

<style scoped lang="scss">
.dupl-header {
  background-color: var(--v-default-base);
}

// css to scroll infinite
   .my-scroller {
     height: 80vh;
     width: 100%;
     overflow-y: scroll;
     padding-right: 10px;

&::-webkit-scrollbar {
   width: 6px;
 }

&::-webkit-scrollbar-track {
   background: transparent;
 }

&::-webkit-scrollbar-thumb {
   background-color: var(--v-greyTint-base);
   border-radius: 36px;
   border: none;
 }
}
</style>